import type { CapturePointHost, HostFeature } from '../shared/host-capabilities';
import type { HostWords } from '../shared/host-words';
import { isPageAddress } from './page-address';

/**
 * What Paige's browser host hides of the desktop workbench: everything that only makes sense on
 * the operator's own Windows machine (IMPLEMENTATION.md sell-ready sprint 2, row 18). Where Paige
 * has the cloud equivalent the screen points at it instead: the job's "Send results to"
 * destinations for the export card, the Paige Scan sheet for the scanner. A folder is not hidden:
 * the browser's folder picker uploads it (folder-picker.ts, row 22); only a folder *watched* on a
 * PC's disk is desktop-only, and that is Paige Scan's, never the workbench's. The table with one
 * line per entry is in docs/engineering/workbench-port.md.
 */
export const paigeHiddenFeatures: readonly HostFeature[] = [
  'local-performance',
  'shared-processing',
  'local-storage',
  'software-updates',
  'licensing',
  'saved-connections',
  'desktop-export',
  'detached-panels',
  'interface-zoom',
  'learning-schedule',
  'workbench-settings',
  'job-library',
  'not-read-retry',
  'setup-import-phase',
  'ctrl-page-keys',
  'window-keys',
  'support-access',
  'scanner-feed',
  'legacy-import',
  'orange-accent',
  'job-delete',
  'job-pause',
  // Not 'confirm-rejudge' (2026-10-20): the engine host re-judges held work in the background as
  // confirmations arrive (Paige.Engine.Host HostBackground.Rejudger, cpv6-parity H-07), so "One
  // confirm can clear many" is true in Paige.
  'watched-folder',
];

/**
 * Paige's word for the thing a customer opens. CapturePoint's operator sits at a scanner and opens
 * a "scan job"; a Paige customer's pages arrive from email, a folder, a scanner or the API, and most
 * of them own no scanner at all, so the word is "job" (docs/engineering/glossary.md). The first
 * screen after sign-up was telling them to "Choose a scan job".
 *
 * The rest is said where the desktop's words are about the operator's own computer: setup running
 * out of memory, and two setup tips whose thing Paige has in its own form (its destinations for the
 * desktop's export; a job setup downloaded and uploaded through the browser, job-setup-files.ts).
 */
export const paigeWords: HostWords = {
  job: 'job',
  jobs: 'jobs',
  jobCapital: 'Job',
  jobsCapital: 'Jobs',
  setupOutOfMemory:
    'Cortexa ran out of room for this setup, so it stopped. Your documents were not changed. Try again, or set up from fewer documents.',
  tips: {
    export: {
      description:
        'Paige sends each finished document to every place this job sends to, and keeps it ready to download too.',
      chips: [
        'SharePoint',
        'OneDrive',
        'Google Drive',
        'Dropbox',
        'Box',
        'Content Central',
        'Nucleus One',
        'A download',
      ],
    },
    carry: {
      line: 'Start another job from what it learned.',
      description:
        'Download a job’s setup, with everything it learned, and upload it to start a new job from it. No documents go with it.',
    },
  },
};

export const paigeHost: CapturePointHost = {
  hidden: paigeHiddenFeatures,
  words: paigeWords,
  pagePreviewSource: isPageAddress,
};

/** Installs Paige as the host before the App renders; the renderer reads it through `hostHas`. */
export function installPaigeHost(): void {
  window.capturePointHost = paigeHost;
}
